import React from "react";
import EmptyImage from "../EmptyImage.tsx";
import { Incident, Unschedule } from "../../models/Incidents.ts";
import RowIncident from "./RowIncident.tsx";
import UnscheduleRow from "./UnscheduleRow.tsx";
import type { IncidentAfterSavePayload } from "./incidentTableTypes.ts";
import type { BranchReportMeta } from "../../utils/payrollBranchMeta.ts";

export type IncidentsTableProps = {
  records: Incident[];
  unschedule: Unschedule[];
  removeRecord: (scheduleId: number) => void;
  removeUnscheduleRecord: (partnerId: number, checkInId: number) => void;
  onAfterSave?: (payload: IncidentAfterSavePayload) => void;
  loading?: boolean;
  updateIncidentRecord?: (scheduleId: number, updates: { type?: string; reason?: string }) => void;
  incidentViewMode?: "pending" | "resolved" | "all";
  onViewModeChange?: (mode: "pending" | "resolved" | "all") => void;
  isAdmin?: boolean;
  hidePlannedColumns?: boolean;
  branchMetaMap?: Record<number, BranchReportMeta>;
  maxNightCutoffMinutes?: number;
  /** Día operativo de la consulta (incidencias por sucursal / jefe). */
  operationDate?: string;
};

const Incidents = ({
  records,
  unschedule,
  removeRecord,
  removeUnscheduleRecord,
  onAfterSave,
  loading = false,
  updateIncidentRecord,
  incidentViewMode = 'pending',
  onViewModeChange,
  isAdmin = false,
  hidePlannedColumns = false,
  branchMetaMap = {},
  maxNightCutoffMinutes,
  operationDate,
}: IncidentsTableProps) => {
  const totalIncidencias = records.length;
  const totalNoProgramados = unschedule.length;
  const totalGeneral = totalIncidencias + totalNoProgramados;

  return (
    <div>
      <div
        className="incidents-summary-card p-3 p-md-4"
        style={{
          background: "linear-gradient(135deg, #495057 0%, #343a40 100%)",
          borderRadius: "12px",
          marginBottom: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "white",
            }}
          >
            <i className="fa fa-exclamation-triangle" style={{ fontSize: "20px", color: "#ffc107" }}></i>
            <span style={{ fontSize: "16px", fontWeight: "600" }}>
              Total de incidencias: 
            </span>
            <span
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#ffc107",
                marginLeft: "4px",
              }}
            >
              {totalGeneral}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "white",
            }}
          >
            <i className="fa fa-calendar-check" style={{ fontSize: "16px", opacity: 0.8 }}></i>
            <span style={{ fontSize: "14px", opacity: 0.9 }}>Programadas:</span>
            <span
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginLeft: "4px",
              }}
            >
              {totalIncidencias}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "white",
            }}
          >
            <i className="fa fa-clock-rotate-left" style={{ fontSize: "16px", opacity: 0.8 }}></i>
            <span style={{ fontSize: "14px", opacity: 0.9 }}>No programados:</span>
            <span
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginLeft: "4px",
              }}
            >
              {totalNoProgramados}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs para filtrar incidencias */}
      {onViewModeChange && (
        <div
          className="d-flex gap-2 gap-md-3 mb-3 p-2 p-md-3"
          style={{
            background: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
          }}
        >
          <button
            onClick={() => onViewModeChange('pending')}
            style={{
              flex: 1,
              padding: "8px 10px",
              fontSize: "13px",
              fontWeight: "600",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              backgroundColor: incidentViewMode === 'pending' ? "#495057" : "#f8f9fa",
              color: incidentViewMode === 'pending' ? "white" : "#495057",
              boxShadow: incidentViewMode === 'pending' ? "0 2px 4px rgba(0,0,0,0.2)" : "none",
            }}
            onMouseEnter={(e) => {
              if (incidentViewMode !== 'pending') {
                e.currentTarget.style.backgroundColor = "#e9ecef";
              }
            }}
            onMouseLeave={(e) => {
              if (incidentViewMode !== 'pending') {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
              }
            }}
          >
            Pendientes
          </button>
          <button
            onClick={() => onViewModeChange('resolved')}
            style={{
              flex: 1,
              padding: "8px 10px",
              fontSize: "13px",
              fontWeight: "600",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              backgroundColor: incidentViewMode === 'resolved' ? "#495057" : "#f8f9fa",
              color: incidentViewMode === 'resolved' ? "white" : "#495057",
              boxShadow: incidentViewMode === 'resolved' ? "0 2px 4px rgba(0,0,0,0.2)" : "none",
            }}
            onMouseEnter={(e) => {
              if (incidentViewMode !== 'resolved') {
                e.currentTarget.style.backgroundColor = "#e9ecef";
              }
            }}
            onMouseLeave={(e) => {
              if (incidentViewMode !== 'resolved') {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
              }
            }}
          >
            Resueltas
          </button>
          <button
            onClick={() => onViewModeChange('all')}
            style={{
              flex: 1,
              padding: "8px 10px",
              fontSize: "13px",
              fontWeight: "600",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              backgroundColor: incidentViewMode === 'all' ? "#495057" : "#f8f9fa",
              color: incidentViewMode === 'all' ? "white" : "#495057",
              boxShadow: incidentViewMode === 'all' ? "0 2px 4px rgba(0,0,0,0.2)" : "none",
            }}
            onMouseEnter={(e) => {
              if (incidentViewMode !== 'all') {
                e.currentTarget.style.backgroundColor = "#e9ecef";
              }
            }}
            onMouseLeave={(e) => {
              if (incidentViewMode !== 'all') {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
              }
            }}
          >
            Todas
          </button>
        </div>
      )}

      <div
        className="table-sticky mb-3"
        style={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          maxHeight: "calc(100vh - 280px)",
          overflowY: "auto",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <table
          className="table table-hover align-middle mb-0 incidents-data-table"
          style={{
            fontSize: "12px",
            minWidth: "760px",
            width: "100%",
            backgroundColor: "white",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <thead
            className="sticky-thead text-center"
            style={{
              background: "#495057",
              borderTop: "1px solid #dee2e6",
              borderBottom: "1px solid #dee2e6",
              borderLeft: "none",
              borderRight: "none",
              color: "white",
              boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
            }}
          >
            <tr>
              <th
                rowSpan={2}
                className="align-middle"
                style={{
                  padding: "6px 8px",
                  fontWeight: "600",
                  fontSize: "11px",
                  color: "white",
                  borderLeft: "none",
                  borderRight: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Colaborador
              </th>
              <th
                colSpan={2}
                className="align-middle"
                style={{
                  padding: "6px 8px",
                  fontWeight: "600",
                  fontSize: "11px",
                  color: "white",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Horario Planeado
              </th>
              <th
                colSpan={2}
                className="align-middle"
                style={{
                  padding: "6px 8px",
                  fontWeight: "600",
                  fontSize: "11px",
                  color: "white",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Incidencias
              </th>
              <th
                rowSpan={2}
                className="align-middle"
                style={{
                  padding: "6px 8px",
                  fontWeight: "600",
                  fontSize: "11px",
                  color: "white",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRight: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Tipo
              </th>
              <th
                rowSpan={2}
                className="align-middle"
                style={{
                  padding: "6px 8px",
                  fontWeight: "600",
                  fontSize: "11px",
                  color: "white",
                  borderLeft: "none",
                  borderRight: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Acción
              </th>
            </tr>
            <tr>
              <th
                style={{
                  padding: "4px 6px",
                  fontSize: "10px",
                  fontWeight: "500",
                  color: "white",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Entrada
              </th>
              <th
                style={{
                  padding: "4px 6px",
                  fontSize: "10px",
                  fontWeight: "500",
                  color: "white",
                  borderLeft: "none",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Salida
              </th>
              <th
                style={{
                  padding: "4px 6px",
                  fontSize: "10px",
                  fontWeight: "500",
                  color: "white",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Entrada
              </th>
              <th
                style={{
                  padding: "4px 6px",
                  fontSize: "10px",
                  fontWeight: "500",
                  color: "white",
                  borderLeft: "none",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Salida
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <tr
                  key={`skeleton-${index}`}
                  className="text-center"
                  style={{
                    backgroundColor: index % 2 === 0 ? "white" : "#f8f9fa",
                    borderBottom: "2px solid #dee2e6",
                    minHeight: "120px",
                  }}
                >
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <div className="d-flex flex-column align-items-center">
                      <p className="card-text placeholder-glow mb-2">
                        <span className="placeholder" style={{ width: "120px", height: "16px", display: "block" }}></span>
                      </p>
                      <p className="card-text placeholder-glow mb-0">
                        <span className="placeholder" style={{ width: "150px", height: "12px", display: "block" }}></span>
                      </p>
                    </div>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <p className="card-text placeholder-glow mb-0">
                      <span className="placeholder" style={{ width: "50px", height: "16px", display: "block", margin: "0 auto" }}></span>
                    </p>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <p className="card-text placeholder-glow mb-0">
                      <span className="placeholder" style={{ width: "50px", height: "16px", display: "block", margin: "0 auto" }}></span>
                    </p>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <p className="card-text placeholder-glow mb-0">
                      <span className="placeholder" style={{ width: "60px", height: "16px", display: "block", margin: "0 auto" }}></span>
                    </p>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <p className="card-text placeholder-glow mb-0">
                      <span className="placeholder" style={{ width: "60px", height: "16px", display: "block", margin: "0 auto" }}></span>
                    </p>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle", width: "45%" }}>
                    <div className="d-flex flex-column align-items-center gap-2">
                      <p className="card-text placeholder-glow mb-0">
                        <span className="placeholder" style={{ width: "140px", height: "14px", display: "block", margin: "0 auto" }}></span>
                      </p>
                      <p className="card-text placeholder-glow mb-0">
                        <span className="placeholder" style={{ width: "100%", height: "45px", display: "block", borderRadius: "6px" }}></span>
                      </p>
                      <div className="d-flex gap-2">
                        <span className="placeholder" style={{ width: "60px", height: "22px", display: "block", borderRadius: "12px" }}></span>
                        <span className="placeholder" style={{ width: "60px", height: "22px", display: "block", borderRadius: "12px" }}></span>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <div className="d-flex flex-column align-items-center gap-2">
                      <p className="card-text placeholder-glow mb-0">
                        <span className="placeholder" style={{ width: "80px", height: "14px", display: "block", margin: "0 auto" }}></span>
                      </p>
                      <div className="d-flex gap-2">
                        <span className="placeholder" style={{ width: "38px", height: "38px", display: "block", borderRadius: "50%" }}></span>
                        <span className="placeholder" style={{ width: "38px", height: "38px", display: "block", borderRadius: "50%" }}></span>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <>
                {records.map(
                  (record, index) =>
                    record?.status && (
                      <RowIncident
                        record={record}
                        key={`${record.schedule.id}-${index}`}
                        removeRecord={() => removeRecord(record.schedule.id)}
                        index={index}
                        onAfterSave={onAfterSave}
                        updateIncidentRecord={updateIncidentRecord}
                        isAdmin={isAdmin}
                        branchMetaMap={branchMetaMap}
                        maxNightCutoffMinutes={maxNightCutoffMinutes}
                      />
                    )
                )}
                {unschedule.map((record, index) => {
                  const recordsCount = records.filter(r => r?.status).length;
                  return (
                    <UnscheduleRow
                      unschedule={record}
                      key={`unschedule-${record.partner.id}-${record.checkIn.id}-${index}`}
                      index={recordsCount + index}
                      removeUnscheduleRecord={() =>
                        removeUnscheduleRecord(
                          record.partner.id,
                          record.checkIn.id
                        )
                      }
                      onAfterSave={onAfterSave}
                      branchMetaMap={branchMetaMap}
                      maxNightCutoffMinutes={maxNightCutoffMinutes}
                      operationDate={operationDate}
                    />
                  );
                })}
              </>
            )}
          </tbody>
        </table>

        {Boolean(records.length === 0 && unschedule.length === 0) && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              padding: "40px 20px",
              backgroundColor: "white",
            }}
          >
            <EmptyImage className="mx-auto w-250px" text="Sin incidencias" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Incidents;
